/**
 * Para ONDE o acesso vai, quando ela clica em "Autorizar".
 *
 * ── Por que não o nome nem o site do cliente ─────────────────────────────
 *
 * O Supabase aceita registro dinâmico: qualquer um cadastra um cliente OAuth,
 * e `client_name` e `client_uri` são o que o cliente DIZ de si. Até 05/10 a
 * tela mostrava só esses dois — um cliente registrado como "Claude", com
 * `client_uri` em `claude.ai` e o retorno apontando para outro domínio,
 * aparecia exatamente como o Claude de verdade.
 *
 * O `redirect_uri` é o único campo que não dá para enfeitar: é para lá que o
 * código de autorização é entregue. Quem controla esse endereço recebe o
 * acesso, chame-se como quiser.
 */

/**
 * Os endereços de retorno do Claude e do ChatGPT.
 *
 * Comparação EXATA de host, sem sufixo: `claude.ai.exemplo.com` termina com
 * um nome conhecido e não é ele, e `evilclaude.ai` também não.
 */
export const DESTINOS_CONHECIDOS: Record<string, string> = {
  'claude.ai': 'Claude',
  'claude.com': 'Claude',
  'chatgpt.com': 'ChatGPT',
};

export type Destino =
  /** Um dos dois que a tela "Conectar ao Claude / ChatGPT" ensina a ligar. */
  | { tipo: 'conhecido'; host: string; quem: string }
  /** Um programa rodando no aparelho dela — o Claude Code faz assim. */
  | { tipo: 'este-aparelho'; host: string }
  /** Qualquer outro lugar. Pode ser legítimo; a tela precisa dizer que não sabe. */
  | { tipo: 'desconhecido'; host: string }
  /** Sem endereço legível não há como dizer para onde vai. */
  | { tipo: 'ilegivel' };

const LOCAIS = new Set(['localhost', '127.0.0.1', '[::1]']);

export const destinoDoConsentimento = (redirectUri: string | null | undefined): Destino => {
  let url: URL;
  try { url = new URL(String(redirectUri ?? '')); } catch { return { tipo: 'ilegivel' }; }

  const host = url.hostname.toLowerCase();
  if (!host) return { tipo: 'ilegivel' };

  if (LOCAIS.has(host)) return { tipo: 'este-aparelho', host: url.host };
  // `http://claude.ai` não é o Claude: sem TLS, quem está no caminho lê o código.
  const quem = url.protocol === 'https:' ? DESTINOS_CONHECIDOS[host] : undefined;
  return quem ? { tipo: 'conhecido', host, quem } : { tipo: 'desconhecido', host: url.host };
};
